import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { portalApi, resolveFileUrl, isStoredFileUrl } from '../../../../lib/portalApi';
import { FolderOpen, ExternalLink, CheckCircle } from 'lucide-react';
import { SearchableDocumentsModal, SearchableStartupDocuments } from '../../shared/SearchableStartupDocuments';

export default function ApplicationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showDocsModal, setShowDocsModal] = useState(false);

  const load = () => portalApi.getApplication(id).then(setApp).catch((e) => setError(e.message));
  useEffect(() => {
    load();
  }, [id]);

  const act = async (fn, after) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      if (after) after();
      else await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!app && !error) return <p className="text-white/40">Loading…</p>;
  if (error && !app) return <p className="text-red-400">{error}</p>;

  const decided = ['accepted', 'rejected'].includes(app.status);
  const answerEntries = Object.entries(app.labeledAnswers || {}).filter(
    ([k]) => !k.endsWith('__meta')
  );
  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <Link to="/admin/applications" className="text-sm text-white/50 hover:text-[#ff8ec5]">
          ← Applications
        </Link>
        <button
          type="button"
          onClick={() => setShowDocsModal(true)}
          className="px-3.5 py-1.5 rounded-xl border border-white/15 hover:border-[#d42b92]/50 hover:bg-[#d42b92]/10 text-white text-xs font-medium flex items-center gap-1.5 transition-colors"
        >
          <FolderOpen className="w-3.5 h-3.5 text-[#ff8ec5]" />
          Documents Repository ({app.documents?.length || 0})
        </button>
      </div>

      <div>
        <h1 className="text-2xl font-semibold text-white">{app.companyName}</h1>
        <p className="text-white/50 mt-1">
          {app.fullName} · {app.businessEmail} · {app.phoneE164}
        </p>
        <p className="mt-2 text-sm text-[#ff8ec5] capitalize">{app.status.replace('_', ' ')}</p>
      </div>

      <div className="rounded-2xl border border-white/10 p-5 space-y-3">
        <h3 className="text-white font-medium">Answers</h3>
        {answerEntries.length === 0 && (
          <p className="text-white/40 text-sm">No answers.</p>
        )}
        {answerEntries.map(([k, v]) => (
          <div key={k}>
            <div className="text-xs text-white/40 uppercase tracking-wide">{k}</div>
            <div className="text-white/80 mt-0.5">
              {isStoredFileUrl(v) ? (
                <a
                  href={resolveFileUrl(v)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#ff8ec5] hover:underline"
                >
                  Open file
                </a>
              ) : (
                String(Array.isArray(v) ? v.join(', ') : v)
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-white/10 p-5">
        <SearchableStartupDocuments
          applicationId={id}
          title="Application Documents"
        />
      </div>

      <SearchableDocumentsModal
        isOpen={showDocsModal}
        onClose={() => setShowDocsModal(false)}
        applicationId={id}
        title={`${app.companyName} · Application Documents`}
      />

      {!decided && (
        <div className="space-y-3">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Decision note (optional)"
            className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white text-sm min-h-[80px]"
          />
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="flex flex-wrap gap-2">
            <button
              disabled={busy}
              onClick={() => act(() => portalApi.underReviewApplication(id))}
              className="px-4 py-2 rounded-xl border border-white/15 text-white text-sm disabled:opacity-50"
            >
              Under review
            </button>
            <button
              disabled={busy}
              onClick={() =>
                act(() => portalApi.acceptApplication(id, note), () =>
                  navigate(`/admin/onboarding`)
                )
              }
              className="px-4 py-2 rounded-xl bg-[#d42b92] text-white text-sm disabled:opacity-50"
            >
              Accept
            </button>
            <button
              disabled={busy}
              onClick={() => act(() => portalApi.rejectApplication(id, note))}
              className="px-4 py-2 rounded-xl bg-red-600/80 text-white text-sm disabled:opacity-50"
            >
              Reject
            </button>
          </div>
        </div>
      )}

      {app.status === 'accepted' && app.startupId && (
        <Link
          to={`/admin/onboarding/${app.startupId}`}
          className="inline-block text-[#ff8ec5] hover:underline text-sm"
        >
          Open onboarding board →
        </Link>
      )}
    </div>
  );
}
